import ButtonGroup from '@/global/components/ButtonGroup'
import { Checkbox } from '@/global/components/Checkbox'
import { Combobox } from '@/global/components/Combobox'
import { Button } from '@/global/components/ui/button'
import { cn } from '@/lib/utils'
import { CategoriaEnum } from '@acme/auth'
import { filterLocaisCentro } from '@acme/common'
import dayjs from 'dayjs'
import { useAuth, useUrlState } from 'hooks'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Funcionario, funcionario as funcionario_api } from 'service/api'
import { useStore } from 'store'
import { HELPER_TEXT_FORM, SelectProps } from './types'

interface IProps extends Omit<SelectProps, 'variant'> {
    /**
     * `form` = preset para formulários: largura 100%, size `medium` e
     * helperText reservado para manter o alinhamento com os demais campos.
     */
    variant?: SelectProps['variant'] | 'form'
    centroCustoId?: number
    localId?: number
    children?: never
    /**
     * Hook que se atualiza quando há ou não funcionários sendo listados no Select
     */
    hasFuncionariosRef?: React.MutableRefObject<boolean>
    /**
     * Callback para notificar quando a lista de funcionários está vazia ou preenchida.
     */
    onHasFuncionariosChange?: (hasFuncionarios: boolean) => void
    /** Exibe checkbox "Mostrar demitidos" que inclui rescindidos na lista */
    permitirDemitidos?: boolean
}

export function SelectFuncionario({
    size,
    width,
    button: hasButtons = false,
    disabled,
    centroCustoId,
    localId,
    variant = 'filled',
    hasFuncionariosRef,
    onHasFuncionariosChange,
    permitirDemitidos = false,
    className,
    helperText,
}: IProps) {
    const isForm = variant === 'form'
    const selectSize = size ?? (isForm ? 'medium' : 'small')
    const selectWidth = width ?? (isForm ? '100%' : 260)
    const selectHelperText =
        helperText ?? (isForm ? HELPER_TEXT_FORM : undefined)

    const {
        decoded: { categoria },
    } = useAuth()
    const diasRescisao = useStore(state => state.company?.diasRescisao)
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)

    const [{ searchFuncionario }, setUrlState] = useUrlState({
        searchFuncionario: '',
    })
    const [inputValue, setInputValue] = useState(searchFuncionario)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const handleInputChange = useCallback(
        (value: string) => {
            setInputValue(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                setUrlState({ searchFuncionario: value || (null as any) })
            }, 300)
        },
        [setUrlState],
    )

    useEffect(() => {
        setInputValue(searchFuncionario)
    }, [searchFuncionario])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const queryParams: Record<string, unknown> = { empresaId }
    if (centroCustoId && centroCustoId > 0)
        queryParams.centroCustoId = centroCustoId
    if (localId && localId > 0) queryParams.localId = localId
    if (searchFuncionario) queryParams.search = searchFuncionario

    const {
        data: pages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
    } = funcionario_api.useInfiniteScroll({
        queryParams,
        enabled: empresaId > 0,
        perPage: 25,
    })

    const allLoaded = useMemo(
        () => (pages?.pages.flatMap(p => p.data) ?? []) as Funcionario[],
        [pages],
    )

    const exibirApenasAtivos = categoria !== CategoriaEnum.Funcionario
    const [mostrarDemitidos, setMostrarDemitidos] = useState(false)

    const isDemitido = useCallback(
        (obj: Funcionario) =>
            !!obj.dtRescisao &&
            dayjs(obj.dtRescisao).isBefore(
                dayjs().subtract(diasRescisao || 40, 'day'),
                'day',
            ),
        [diasRescisao],
    )

    const data = React.useMemo(
        () =>
            allLoaded.filter(obj => {
                if (
                    exibirApenasAtivos &&
                    !mostrarDemitidos &&
                    isDemitido(obj)
                ) {
                    return false
                }

                return filterLocaisCentro<typeof obj>(
                    localId && localId > 0 ? [localId] : [],
                    [centroCustoId!],
                    it => it as any,
                )(obj)
            }),
        [
            allLoaded,
            exibirApenasAtivos,
            mostrarDemitidos,
            isDemitido,
            centroCustoId,
            localId,
        ],
    )

    const isPrimeiroFuncionario = useMemo(() => {
        if (data.length === 0) return true
        const index = data.findIndex(f => f.id === funcionarioId)
        return index === 0
    }, [data, funcionarioId])

    const isUltimoFuncionario = useMemo(() => {
        if (data.length === 0) return true
        const index = data.findIndex(f => f.id === funcionarioId)
        return index === data.length - 1 && !hasNextPage
    }, [data, funcionarioId, hasNextPage])

    const [isNavigating, setIsNavigating] = useState(false)

    async function next() {
        const index = data.findIndex(item => item.id === funcionarioId)
        if (index !== -1 && index < data.length - 1) {
            setEmployeeId(data[index + 1].id)
        } else if (hasNextPage) {
            setIsNavigating(true)
            const result = await fetchNextPage()
            setIsNavigating(false)
            const newItems = result.data?.pages.at(-1)?.data as
                | Funcionario[]
                | undefined
            if (newItems?.length) {
                setEmployeeId(newItems[0].id)
            }
        }
    }

    async function previous() {
        const index = data.findIndex(item => item.id === funcionarioId)
        if (index > 0) {
            setEmployeeId(data[index - 1].id)
        }
    }

    // Funcionário selecionado pode não estar nas páginas carregadas
    // (fora da primeira página, filtrado por local/centro de custo ou busca)
    const { data: funcionarioForaDaLista } =
        funcionario_api.useFetchById<Funcionario>(funcionarioId, {
            enabled:
                funcionarioId > 0 && !data.some(f => f.id === funcionarioId),
        })

    const selectedFuncionario = useMemo(
        () =>
            data.find(f => f.id === funcionarioId) ??
            (funcionarioForaDaLista?.id === funcionarioId
                ? funcionarioForaDaLista
                : null),
        [data, funcionarioId, funcionarioForaDaLista],
    )

    const carregarMais = useCallback(() => {
        if (!isFetchingNextPage) fetchNextPage()
    }, [fetchNextPage, isFetchingNextPage])

    const exibirCheckboxDemitidos = permitirDemitidos && exibirApenasAtivos

    // Com o filtro de demitidos ativo, um demitido selecionado ficaria
    // invisível no select mas continuaria no store — redireciona para o
    // primeiro ativo disponível.
    useEffect(() => {
        if (mostrarDemitidos || !exibirApenasAtivos || funcionarioId === -1)
            return
        const selecionado = allLoaded.find(f => f.id === funcionarioId)
        if (selecionado && isDemitido(selecionado)) {
            setEmployeeId(data[0]?.id ?? -1)
        }
    }, [mostrarDemitidos, allLoaded])

    // Set default employee if none selected
    useEffect(() => {
        const hasFuncionarios = data.length > 0

        if (hasFuncionariosRef?.current !== undefined) {
            hasFuncionariosRef.current = hasFuncionarios
        }

        onHasFuncionariosChange?.(hasFuncionarios)

        if (funcionarioId === -1 && data.length > 0) {
            setEmployeeId(data[0].id)
        }
    }, [data, funcionarioId, hasFuncionariosRef, onHasFuncionariosChange])

    return (
        <div
            className={cn('flex items-center gap-2', className)}
            style={{ width: selectWidth }}
        >
            <Combobox
                className="min-w-0 flex-1"
                label="Funcionário"
                size={selectSize}
                variant={variant === 'outlined' ? 'outlined' : 'filled'}
                placeholder="Selecione um funcionário"
                options={data}
                value={selectedFuncionario}
                onChange={value => {
                    if (!value) return
                    setEmployeeId(value.id)
                    setUrlState({ searchFuncionario: null as any })
                }}
                getOptionLabel={option =>
                    option
                        ? `${option.nome}${
                              exibirApenasAtivos && isDemitido(option)
                                  ? ' (Demitido)'
                                  : ''
                          }`
                        : ''
                }
                getOptionKey={option => String(option.id)}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                // busca e paginação são do servidor
                inputValue={inputValue}
                onInputChange={handleInputChange}
                loading={isFetching}
                hasMore={hasNextPage}
                onLoadMore={carregarMais}
                loadingMore={isFetchingNextPage}
                disabled={disabled}
                noOptionsText="Nenhum funcionário encontrado"
                footer={
                    exibirCheckboxDemitidos ? (
                        <Checkbox
                            label="Mostrar demitidos"
                            checked={mostrarDemitidos}
                            onCheckedChange={marcado =>
                                setMostrarDemitidos(marcado === true)
                            }
                        />
                    ) : undefined
                }
                helperText={selectHelperText}
            />
            {hasButtons && (
                <ButtonGroup>
                    <Button
                        type="button"
                        variant="outline"
                        title="Funcionário anterior"
                        aria-label="Funcionário anterior"
                        disabled={isPrimeiroFuncionario || isNavigating}
                        onClick={previous}
                        className="h-12 w-12"
                    >
                        <ChevronLeftIcon className="text-foreground" />
                    </Button>
                    <Button
                        type="button"
                        variant="outline"
                        title="Próximo funcionário"
                        aria-label="Próximo funcionário"
                        disabled={isUltimoFuncionario || isNavigating}
                        onClick={next}
                        className="h-12 w-12"
                    >
                        <ChevronRightIcon className="text-foreground" />
                    </Button>
                </ButtonGroup>
            )}
        </div>
    )
}
